import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  ViewChild
} from '@angular/core';

import {
  MAT_DIALOG_DATA,
  MatDialogRef
} from '@angular/material/dialog';

import {
  BrowserQRCodeReader
} from '@zxing/browser';

import { QrDataService, QrZuordnungFehler } from '../../../../../Services/offline/qr-data.service';
import { MessstelleQrInfo } from '../../../../../Models/messstelle-qr.model';


export interface MessstelleQrScannerDialogData {

  messstelleId: number;

  ersetzen: boolean;
}

/** Was der Dialog beim Schließen zurückgibt, wenn ein Code zugeordnet wurde. */
export interface MessstelleQrScannerErgebnis {
  qr: MessstelleQrInfo;
  ersetzt: boolean;

  /** True: ohne Verbindung lokal gespeichert, Abgleich folgt bei Verbindung. */
  lokal: boolean;
}


/**
 * Scannt einen Sticker aus dem QR-Pool und ordnet ihn der Messstelle zu.
 *
 * Online wie bisher über den Server. Ohne Verbindung speichert der
 * Datendienst die Zuordnung lokal und prüft dabei gegen alles, was auf dem
 * Gerät bekannt ist – derselbe Code darf nicht zwei Messstellen gehören.
 * Die endgültige Prüfung macht der Server beim Abgleich.
 */
@Component({
  selector:
    'app-messstelle-qr-scanner-dialog',

  templateUrl:
    './messstelle-qr-scanner-dialog.component.html',

  styleUrls: [
    './messstelle-qr-scanner-dialog.component.css'
  ]
})
export class MessstelleQrScannerDialogComponent
  implements AfterViewInit, OnDestroy {


  @ViewChild(
    'video',
    {
      static: true
    }
  )
  video!:
    ElementRef<HTMLVideoElement>;


  private readonly reader =
    new BrowserQRCodeReader();


  private controls:
    {
      stop: () => void;
    }
    | null =
    null;


  private verarbeitet =
    false;


  saving =
    false;


  fehler =
    '';


  constructor(

    private qr: QrDataService,

    private dialogRef:
      MatDialogRef<MessstelleQrScannerDialogComponent, MessstelleQrScannerErgebnis | undefined>,

    @Inject(MAT_DIALOG_DATA)
    public data:
      MessstelleQrScannerDialogData

  ) { }


  ngAfterViewInit(): void {

    this.startScanner();
  }


  private async startScanner():
    Promise<void> {

    this.fehler = '';

    this.verarbeitet = false;


    try {

      this.controls =
        await this.reader
          .decodeFromVideoDevice(

            undefined,

            this.video.nativeElement,

            result => {

              if (
                !result
                ||
                this.verarbeitet
              ) {
                return;
              }


              this.verarbeitet =
                true;


              this.controls?.stop();


              this.uebernehmen(
                result.getText()
              );
            }
          );

    }
    catch (err) {

      console.error(
        'QR-Scanner konnte nicht gestartet werden:',
        err
      );


      this.fehler =
        'Die Kamera konnte nicht gestartet werden. Bitte prüfe die Kameraberechtigung.';
    }
  }


  private uebernehmen(
    wert: string
  ): void {

    if (this.saving) {
      return;
    }

    this.saving = true;
    this.fehler = '';

    this.qr
      .zuordnen(this.data.messstelleId, wert, this.data.ersetzen)
      .subscribe({

        next: ergebnis => {

          this.saving = false;

          // Kamera sicher stoppen
          this.controls?.stop();

          // Dialog schließen und Ergebnis an die Messstellen-Seite zurückgeben
          this.dialogRef.close({
            qr: ergebnis.daten,
            ersetzt: this.data.ersetzen,
            lokal: ergebnis.lokal
          });
        },

        error: err => {

          console.error(
            'QR-Code konnte nicht zugeordnet werden:',
            err
          );

          this.saving = false;

          this.fehler =
            err instanceof QrZuordnungFehler
              ? err.message
              : err?.error?.message
                ?? err?.message
                ?? 'Der QR-Code konnte nicht zugewiesen werden.';

          /*
           * WICHTIG:
           * Nicht automatisch neu starten.
           *
           * Sonst scannt die Kamera sofort wieder
           * denselben QR-Code und man hängt
           * in einer Fehlerschleife.
           */
        }

      });
  }

  erneutScannen(): void {

    if (this.saving) {
      return;
    }

    this.fehler = '';

    this.startScanner();
  }


  schliessen(): void {

    this.dialogRef.close();
  }


  ngOnDestroy(): void {

    this.controls?.stop();
  }


}
